import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { LanguagesDropdown } from "../LanguagesDropdown";
import { MachineCenterContext } from "../../contexts/MachineCenterContext/MachineCenterContext";
import { HMIContext } from "../../contexts/HMIContext/HMIContext";
import { OperatorsDropdown } from "../OperatorsDropdown/OperatorsDropdown.view";
import { DowntimeButton } from "../DowntimeButton/DowntimeButton.view";
import { UptimeButton } from "../UptimeButton/UptimeButton.view";
import { UserContext } from "../../contexts/UserContext/UserContext";
import { Alert, Grid, Snackbar, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { useStyles } from "./Navbar.styles";
import { DowntimeCounter } from "../DowntimeCounter/DowntimeCounter.view";
import {
    DOWNTIMEMETHODCAPTURE,
    FAILUREDETAILSSTEPS,
    HMI_PATHS,
    LocalStorageCachedDataKeysCluster,
    LocalStorageCachedDataKeysHMI,
    LocalStorageGlobalKeys,
} from "../../constants/constants";
import { HMICachedDataContext } from "../../contexts/HMICachedDataContext/HMICachedDataContext";
import { useLocation } from "react-router-dom";
import { NoInternetConnectionWarning } from "../NoInternetConnectionWarning/NoInternetConnectionWarning.view";
import { NoPLCConnectionWarning } from "components/NoPLCConnectionWarning/NoPLCConnectionWarning.view";
import { DataLastUpdated } from "components/DataLastUpdated/DataLastUpdated.view";
import { getToday, truncateLongText } from "utilities/utilities";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import {
    getClusterIdHMI,
    getCurrentStartBelowTriggerDowntimeCount,
    getCurrentStartDowntime,
    getMachineCenterIdHMI,
    getMachineCentersClusterStatusLocalStorage,
    resetLocalStorage,
} from "utilities/storageUtilities";
import { OnlineStatusContext } from "contexts/OnlineStatusContext/OnlineStatusContext";
import { useTranslation } from "react-i18next";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import { MQTTListenerContext } from "contexts/MQTTListenerContext/MQTTListenerContext";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import { IUser } from "contexts/UserContext/UserContextInterfaces";
import { ClusterContext } from "contexts/ClusterContext/ClusterContext";
import useAutoNavigateClusterHMI from "hooks/useAutoNavigateClusterHMI";
import moment from "moment";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { NoLocalServerConnectionWarning } from "components/NoLocalServerConnectionWarning/NoLocalServerConnectionWarning.view";
import DragIcon from "components/DragIcon";
import { ClusterDragDropContext } from "pages/HMI/ClusterDashboard/context/ClusterDragDropContextProvider";
import { ReorderingModeControls } from "components/Navbar/component/ReorderingModeControls";

export const Navbar = () => {
    const {
        store,
        dispatch: { resetStore },
    } = useContext(HMICachedDataContext);
    const { store: clusterStore } = useContext(ClusterCachedDataContext);
    const { machineCentersClusterStatus } = useContext(ClusterContext);
    const { toggleDragMode, isDragModeActive, isSaving, cancelDragMode } = useContext(ClusterDragDropContext);
    const { isListening } = useContext(MQTTListenerContext);
    const { machineStatus, plc, downtimeCounterBelowTrigger, handleResetMachineCenterContext } =
        useContext(MachineCenterContext);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const { tokenAvailable } = useContext(UserContext);
    const { failureDetailsStep, resetHMIContextProvider } = useContext(HMIContext);
    const { handleMachineInactiveDowntime, handleClusterLogin } = useUptimeDowntimeActionsHMI();
    const location = useLocation();
    const isHMIDashboard = useMemo(
        () => location.pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.DASHBOARD}`,
        [location.pathname],
    );
    const isClusterDashboard = useMemo(
        () => location.pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.CLUSTER}`,
        [location.pathname],
    );
    const today = getToday();
    const classes = useStyles();
    const isPLC =
        store.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC &&
        Boolean(store.machineCenter?.plc_address);
    const { onlineStatus, updateOnlineStatus } = useContext(OnlineStatusContext);
    const { isBrokerConnected, isLocalServerActive, handleSecretLogoTap } = useContext(MQTTBrokerContext);
    const { t } = useTranslation();

    const isClusterAllowed = useMemo(
        () =>
            store.machineCenter?.production_line?.mill?.cluster_enabled ||
            clusterStore.machineCentersCluster[0]?.production_line?.mill?.cluster_enabled,
        [store, clusterStore],
    );
    const isCluster = useMemo(
        () => getClusterIdHMI() && isClusterAllowed && isClusterDashboard,
        [isClusterAllowed, isClusterDashboard],
    );
    const isClusterHMI = useMemo(
        () => getMachineCenterIdHMI() && getClusterIdHMI() && isClusterAllowed && isHMIDashboard,
        [isClusterAllowed, isHMIDashboard],
    );

    const clusterOperators = useMemo(
        () => clusterStore.clusterDetails?.user_cluster?.map((user) => user) || ([] as IUser[]),
        [clusterStore.clusterDetails],
    );

    const plcState: boolean = useMemo(() => isPLC && isListening && plc, [isPLC, isListening, plc]);

    const connectivityWarnings = useCallback(
        (cluster: boolean) => {
            if (!isBrokerConnected || !isLocalServerActive) {
                return (
                    <Grid
                        item
                        xs={cluster ? 3.5 : 12}
                        style={cluster ? { backgroundColor: "#F5FAFF" } : undefined}
                        className={classes.indicatorGrid}
                    >
                        <NoLocalServerConnectionWarning isBrokerConnected={isBrokerConnected} />
                    </Grid>
                );
            } else if (!onlineStatus) {
                return (
                    <Grid
                        item
                        xs={cluster ? 3.5 : 12}
                        style={cluster ? { backgroundColor: "#F5FAFF" } : undefined}
                        className={classes.indicatorGrid}
                    >
                        <NoInternetConnectionWarning />
                    </Grid>
                );
            }
        },
        [isBrokerConnected, isLocalServerActive, onlineStatus, classes],
    );

    const handleMovingToCluster = useCallback(async () => {
        try {
            setShowErrorMessage(false);
            const localStorageKeyExceptions = [
                LocalStorageGlobalKeys.LANGUAGES,
                LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION,
                LocalStorageGlobalKeys.TOKEN,
                ...Object.values(LocalStorageCachedDataKeysCluster),
                LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS,
                LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES,
                LocalStorageCachedDataKeysHMI.OPERATOR,
                LocalStorageCachedDataKeysHMI.LAST_INTERNET_CONNECTION_RECOVERY_DATE,
                LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED,
                LocalStorageCachedDataKeysHMI.EDITIONS_PENDING,
            ];
            const currentStatusDetails = machineCentersClusterStatus || getMachineCentersClusterStatusLocalStorage();
            // The HMI is thought to work as a closed network offline first, so this is only left as a precaution in the possible case of
            // not having the local stored data at hand, we check for connectivity and proceed to update.
            const isOnline = currentStatusDetails ? false : await updateOnlineStatus();
            await handleClusterLogin(
                clusterStore.machineCentersCluster?.map((machineCenter) => ({
                    machine_center_id: machineCenter.machine_center_id,
                })),
                isOnline,
                currentStatusDetails,
            );
            resetLocalStorage(localStorageKeyExceptions);
            handleResetMachineCenterContext();
            resetHMIContextProvider();
            resetStore();
        } catch {
            setShowErrorMessage(true);
        }
    }, [
        handleResetMachineCenterContext,
        resetHMIContextProvider,
        clusterStore.machineCentersCluster,
        handleClusterLogin,
        machineCentersClusterStatus,
        updateOnlineStatus,
        resetStore,
    ]);

    const { timeRemaining, resetTimer, shouldShowTimer } = useAutoNavigateClusterHMI(
        handleMovingToCluster,
        !!isClusterHMI,
    );

    useEffect(() => {
        if (tokenAvailable) {
            setUserLoggedIn(true);
            return;
        }

        setUserLoggedIn(false);
    }, [tokenAvailable, onlineStatus]);

    const downtimeTriggerThreshold = useMemo(
        () =>
            store.machineCenter?.downtime_trigger_threshold
                ? store.machineCenter?.downtime_trigger_threshold * 1000
                : 0,
        [store.machineCenter?.downtime_trigger_threshold],
    );

    useEffect(() => {
        let interval: ReturnType<typeof setTimeout> | null = null;
        const currentStartDowntimeBelowTrigger = getCurrentStartBelowTriggerDowntimeCount();
        const currentStartDowntime = currentStartDowntimeBelowTrigger
            ? currentStartDowntimeBelowTrigger
            : getCurrentStartDowntime() || 0;

        if (downtimeCounterBelowTrigger && plcState) {
            interval = setInterval(() => {
                let currentTime: Date = new Date();
                const timer = +currentTime - currentStartDowntime;
                if (downtimeTriggerThreshold < timer) {
                    handleMachineInactiveDowntime(currentStartDowntime);
                }
            }, 10);
        } else {
            clearInterval(interval!);
        }

        return () => {
            clearInterval(interval!);
        };
    }, [downtimeCounterBelowTrigger, downtimeTriggerThreshold, plcState, handleMachineInactiveDowntime]);

    return (
        <Grid sx={isCluster ? { height: "100%" } : undefined} container className={classes.gridContainer}>
            {isCluster && (
                <Grid item className={classes.gridItemPlc} xs={6}>
                    <Grid item xs={12}>
                        <Box style={{ height: "100%" }} className={classes.gridBox}>
                            <Grid
                                item
                                xs={8.5}
                                style={{ alignItems: "center" }}
                                className={classes.machineInfoContainer}
                            >
                                <img src="/images/maderaLogo.png" alt="Madera logo" className={classes.maderaLogo} />
                                <Box className={classes.machineNameBox}>
                                    <Typography variant="h1" className={classes.machineName}>
                                        <div className={classes.clusterNameContainer}>
                                            <GridViewOutlinedIcon className={classes.clusterICon} />
                                            <span
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {t("HMI.cluster.title")}:{" "}
                                                {truncateLongText(clusterStore.clusterDetails?.name!, 15)}
                                            </span>
                                        </div>
                                    </Typography>
                                    <Typography variant="subtitle1" className={classes.date}>
                                        <span>{today.toUpperCase()}</span>
                                    </Typography>
                                </Box>
                            </Grid>
                            {connectivityWarnings(Boolean(isCluster))}
                        </Box>
                    </Grid>
                </Grid>
            )}
            {userLoggedIn && store.machineCenter && isHMIDashboard && (
                <Grid
                    item
                    className={
                        (store.machineCenter!.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC && plc) ||
                        store.machineCenter!.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.MANUAL
                            ? classes.gridItemPlc
                            : classes.gridItemPlcError
                    }
                    xs={8}
                >
                    <Grid item xs={12}>
                        <Box className={classes.gridBox}>
                            <Grid item xs={7} className={classes.machineInfoContainer}>
                                <img
                                    src="/images/maderaLogo.png"
                                    alt="Madera logo"
                                    className={classes.maderaLogo}
                                    onPointerDown={handleSecretLogoTap}
                                    style={{
                                        touchAction: "manipulation",
                                        userSelect: "none",
                                        WebkitTapHighlightColor: "transparent",
                                    }}
                                />
                                <Box className={classes.machineNameBox}>
                                    <Typography variant="h1" className={classes.machineName}>
                                        {store.machineCenter.name}
                                    </Typography>
                                    <Typography variant="subtitle1" className={classes.date}>
                                        <span>{today.toUpperCase()}</span>
                                    </Typography>

                                    <DataLastUpdated />
                                </Box>
                            </Grid>
                            <Grid item xs={5}>
                                {connectivityWarnings(Boolean(isCluster))}

                                {!plc &&
                                    store.machineCenter!.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC && (
                                        <Grid item xs={12} className={classes.indicatorGrid}>
                                            <NoPLCConnectionWarning />
                                        </Grid>
                                    )}
                            </Grid>
                        </Box>

                        <Grid container className={classes.downtimeGrid}>
                            <Grid item xs={9.5}>
                                {failureDetailsStep === FAILUREDETAILSSTEPS.DOWNTIMESAVED &&
                                (store.machineCenter!.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
                                    ? !plc
                                    : true) &&
                                !machineStatus ? (
                                    <UptimeButton />
                                ) : (
                                    <DowntimeButton />
                                )}
                            </Grid>
                            {(!machineStatus || (downtimeCounterBelowTrigger && plc)) && (
                                <Grid item xs={2.5}>
                                    <DowntimeCounter />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            )}
            {!isClusterHMI && (
                <Grid
                    item
                    xs={isCluster ? 6 : 4}
                    className={
                        isCluster ? classes.clusterDropdownGrid : `${classes.dropdownsGrid} ${classes.columnDirection}`
                    }
                >
                    {userLoggedIn && store.machineCenter && !isCluster && (
                        <OperatorsDropdown operators={store.machineCenter.users} />
                    )}
                    {userLoggedIn && clusterStore.machineCentersCluster && isClusterDashboard && isCluster ? (
                        !isDragModeActive ? (
                            <>
                                <Box width="45%">
                                    <OperatorsDropdown fullWidth operators={clusterOperators} />
                                </Box>
                                <Box width="45%">
                                    <LanguagesDropdown fullWidth />
                                </Box>

                                <Box
                                    onClick={!isBrokerConnected || !isLocalServerActive ? undefined : toggleDragMode}
                                    sx={!isBrokerConnected || !isLocalServerActive ? undefined : { cursor: "pointer" }}
                                >
                                    <DragIcon disabled={!isBrokerConnected || !isLocalServerActive} />
                                </Box>
                            </>
                        ) : (
                            <ReorderingModeControls
                                onCancel={cancelDragMode}
                                onConfirm={toggleDragMode}
                                isSaving={isSaving}
                            />
                        )
                    ) : (
                        <LanguagesDropdown />
                    )}
                </Grid>
            )}
            {!isCluster && !!isClusterHMI && (
                <Grid item xs={shouldShowTimer ? 4 : 2} className={classes.backToClusterContainer}>
                    <Alert
                        severity="warning"
                        sx={{
                            marginLeft: "16px",
                            display: shouldShowTimer ? "flex" : "none",
                            alignItems: "center",
                        }}
                        onClick={() => resetTimer()}
                    >
                        <Typography style={{ fontFamily: "Open Sans", textAlign: "left" }}>
                            {t("HMI.cluster.inactivity_warning_message")}{" "}
                            <strong>
                                {t("HMI.cluster.inactivity_timer", {
                                    counter: moment.utc(timeRemaining * 1000).format("mm:ss"),
                                })}
                            </strong>
                        </Typography>
                    </Alert>
                    <button onClick={handleMovingToCluster} className={classes.backToClusterButton}>
                        <AppsOutlinedIcon className={classes.backToClusterButtonIcon} />
                    </button>
                </Grid>
            )}
            <Snackbar
                open={showErrorMessage}
                autoHideDuration={6000}
                onClose={() => setShowErrorMessage(false)}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity="error" sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{t("mqtt_broker_provider.error")}</span>
                </Alert>
            </Snackbar>
        </Grid>
    );
};
